import { useEffect, useState } from 'react'
import EmptyState from '../shell/EmptyState'
import StatusCodeForm from './StatusCodeForm'
import { categoryLabel, getStatusCodes, updateStatusCode, type StatusCode, type StatusCodeValues } from '../../pages/admin/catalogStore'

/**
 * docs/product/scheduling-model.md's status codes: "Each code has a short label
 * for board cells, a full name, a category that drives the cell fill and the
 * coverage calculation." Every code with its category, whether it counts as
 * available, and how many schedule entries use it, and a form to change the
 * parts coverage reads.
 */
export default function StatusCodesSection() {
  const [codes, setCodes] = useState<StatusCode[]>([])
  const [loaded, setLoaded] = useState(false)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getStatusCodes().then((loadedCodes) => {
      if (cancelled) return
      setCodes(loadedCodes)
      setLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const editing = codes.find((code) => code.id === editingId) ?? null
  const query = search.trim().toLowerCase()
  const filtered = codes.filter((code) => !query || `${code.name} ${code.code} ${code.shortLabel}`.toLowerCase().includes(query))

  async function save(values: StatusCodeValues) {
    if (!editing) return
    setSaving(true)
    setFormError(null)
    const result = await updateStatusCode(editing.id, values)
    setSaving(false)
    if (!result.ok) {
      setFormError(result.message)
      return
    }
    setCodes(await getStatusCodes())
    setEditingId(null)
    setNotice(`Updated ${editing.name}.`)
  }

  return (
    <section id="status-codes" aria-labelledby="status-codes-heading" className="flex flex-col gap-4">
      <div>
        <h2 id="status-codes-heading" className="text-h3 font-extrabold">
          Status codes
        </h2>
        <p className="mt-1 text-small text-ink-muted">Coverage reads a code&apos;s category and whether it counts as available, never its name.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-surface border border-line bg-canvas p-3">
        <label htmlFor="code-search" className="sr-only">
          Search status codes
        </label>
        <input
          id="code-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by name, code, or short label"
          className="min-h-11 min-w-[240px] flex-1 rounded-control border border-line-strong px-3 text-small"
        />
      </div>

      {notice && (
        <p role="status" className="text-small font-semibold text-success">
          {notice}
        </p>
      )}

      {editing && (
        <StatusCodeForm
          key={editing.id}
          code={editing}
          saving={saving}
          error={formError}
          onSave={save}
          onCancel={() => {
            setEditingId(null)
            setFormError(null)
          }}
        />
      )}

      {!loaded ? (
        <p role="status" className="text-small text-ink-muted">
          Loading&hellip;
        </p>
      ) : filtered.length === 0 ? (
        <EmptyState>No status codes match your search.</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-surface border border-line">
          <table aria-label="Status codes" className="w-full border-collapse">
            <thead>
              <tr>
                {['Name', 'Code', 'Short label', 'Category', 'Available', 'Order', 'Entries'].map((heading) => (
                  <th
                    key={heading}
                    scope="col"
                    className="border-b border-line bg-surface-2 px-3 py-2 text-left text-caption font-bold tracking-wide whitespace-nowrap text-ink-subtle uppercase"
                  >
                    {heading}
                  </th>
                ))}
                <th scope="col" className="border-b border-line bg-surface-2 py-2 pr-2 pl-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((code) => (
                <tr key={code.id}>
                  <th scope="row" className="border-b border-line px-3 py-2 text-left text-small font-bold">
                    {code.name}
                  </th>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted">{code.code}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">{code.shortLabel}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">{categoryLabel(code.category)}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap">{code.countsAsAvailable ? 'Yes' : 'No'}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted [font-variant-numeric:tabular-nums]">{code.sortOrder}</td>
                  <td className="border-b border-line px-3 py-2 text-small whitespace-nowrap text-ink-muted [font-variant-numeric:tabular-nums]">{code.entries}</td>
                  <td className="border-b border-line py-2 pr-2 pl-3 text-small">
                    <div className="flex justify-end">
                      <button
                        type="button"
                        aria-label={`Edit ${code.name}`}
                        onClick={() => {
                          setNotice(null)
                          setFormError(null)
                          setEditingId(code.id)
                        }}
                        className="flex min-h-11 w-14 items-center justify-center rounded-control border border-line-strong px-3 text-small font-bold hover:bg-surface-2"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
